import type {Page, Result} from 'framework';
import type {
  CreateFromCatalogInput,
  CreateManualProductInput,
  ListProductsInput,
  SearchCatalogCandidatesInput,
  UpdateProductInput,
} from '../application/ports/product.repository';
import type {SearchCatalogCandidates} from '../application/use-cases/search-catalog-candidates';
import type {CreateManualProduct} from '../application/use-cases/create-manual-product';
import type {CreateProductFromCatalog} from '../application/use-cases/create-product-from-catalog';
import type {DeleteProduct} from '../application/use-cases/delete-product';
import type {ListProducts} from '../application/use-cases/list-products';
import type {ResolveProduct} from '../application/use-cases/resolve-product';
import type {ScanNutritionLabel} from '../application/use-cases/scan-nutrition-label';
import type {UpdateProduct} from '../application/use-cases/update-product';
import type {CatalogCandidate} from '../domain/catalog-candidate';
import type {NutritionLabelScanResult, Product, ProductId} from '../domain/product';
import type {ProductError} from '../domain/errors';
import type {ResolveResult} from '../domain/resolve-result';

export class ProductController {
  constructor(
    private readonly listProducts: ListProducts,
    private readonly resolveProduct: ResolveProduct,
    private readonly searchCatalogCandidatesUseCase: SearchCatalogCandidates,
    private readonly createFromCatalog: CreateProductFromCatalog,
    private readonly createManual: CreateManualProduct,
    private readonly updateProduct: UpdateProduct,
    private readonly deleteProduct: DeleteProduct,
    private readonly scanNutritionLabel: ScanNutritionLabel,
  ) {}

  list(input: ListProductsInput): Promise<Result<Page<Product>, ProductError>> {
    return this.listProducts.execute(input);
  }

  resolve(barcode: string): Promise<Result<ResolveResult, ProductError>> {
    return this.resolveProduct.execute({barcode});
  }

  searchCatalogCandidates(
    input: SearchCatalogCandidatesInput,
  ): Promise<Result<readonly CatalogCandidate[], ProductError>> {
    return this.searchCatalogCandidatesUseCase.execute(input);
  }

  createFromCatalogProduct(input: CreateFromCatalogInput): Promise<Result<Product, ProductError>> {
    return this.createFromCatalog.execute(input);
  }

  createManualProduct(input: CreateManualProductInput): Promise<Result<Product, ProductError>> {
    return this.createManual.execute(input);
  }

  update(input: UpdateProductInput): Promise<Result<Product, ProductError>> {
    return this.updateProduct.execute(input);
  }

  delete(productId: ProductId): Promise<Result<void, ProductError>> {
    return this.deleteProduct.execute({productId});
  }

  scanLabel(image: File): Promise<Result<NutritionLabelScanResult, ProductError>> {
    return this.scanNutritionLabel.execute({image});
  }
}
